import {merge} from '#core/utils/config-utils.js';

import {PALETTE_IDS} from '#core/application/modules/ids.js';

import {COLOR, FONT} from './theme.js';
import {getBaseComponentsConfig} from './style/base-components';
import {getDialogConfig} from './style/dialogs';
import {getMenuListsConfigs} from './style/menus-links-lists';
import {getPageHeadersConfigs} from './style/interface';
import {getStaticPagesConfig} from './style/static-pages';
import {getVirtualsConfig} from './style/virtuals';
import {getMatchAccaConfig} from './style/matchacca';
import {getBonusesConfig} from './style/bonuses';
import {getBetHistoryConfig} from './style/bethistory';
import {getRacingConfig} from './style/racing';
import {getAccountConfigs} from './style/account';
import {getCardsConfig} from './style/cards';
import {getMarketsConfig} from './style/markets';
import {getBettingCenterConfig} from './style/betting-center';
import {getEventListConfig} from './style/event-list';
import {getSportsContentConfigs} from './style/sports-content';

export function getTokens() {
    const custom = {
        [PALETTE_IDS.APP_VIEW]: {
            BetslipView: {
                betReceipt: {
                    placedBetReceiptView: {
                        shareBet: {
                            showShareButtonLabel: true,
                            shareButton: {
                                buttonLabel: {
                                    container: {
                                        position: 'absolute',
                                        top: 30,
                                        fontSize: 10,
                                    },
                                },
                                innerContainer: {
                                    style: {
                                        flexDirection: 'column',
                                    },
                                },
                            },
                        },
                    },
                },
            },
            InPlayContent: {
                eventsCouponPerTab: {
                    all: {
                        eventList: {
                            header: {
                                _mobile_showMarketSelectors: true,
                            },
                        },
                    },
                },
            },
            HomePage: {
                primaryContentLayout: {
                    _mobile_itemSpacing: 0,
                },
            },
        },
    };

    const palette = {
        [PALETTE_IDS.APP_VIEW]: {
            ACCA_BAR_LABEL_TEXT: {
                fontWeight: FONT.WEIGHT_NORMAL,
                color: COLOR.BRAND.FOREGROUND1,
            },
        },
        [PALETTE_IDS.MOBILE_NAVBAR]: {
            CONTAINER_BG: COLOR.BASE.PAGE,
            LABEL_TEXT: {
                textTransform: 'uppercase',
                color: COLOR.BRAND.FOREGROUND1,
            },
        },
        [PALETTE_IDS.SEARCH_RESULT]: {
            SEARCH_RECENT_ITEM_TEXT: COLOR.BRAND.FOREGROUND1,
            SEARCH_RECENT_ITEM_REMOVE_ICON_COLOR: COLOR.BRAND.FOREGROUND3,

            SEARCH_RESULT_ITEM_ICON_COLOR: COLOR.BRAND.FOREGROUND3,
            SEARCH_RESULT_ITEM_TOURNAMENT_TEXT: COLOR.BRAND.FOREGROUND3,
            SEARCH_RESULT_ITEM_NAME_TEXT: COLOR.BRAND.FOREGROUND1,
            SEARCH_RESULT_ITEM_STATUS_TEXT: COLOR.BRAND.FOREGROUND1,
        },
        [PALETTE_IDS.VIDEO_STREAMING]: {
            VIDEO_STREAMING_CONTAINER_BG: COLOR.BRAND.FOREGROUND2,
            VIDEO_STREAMING_STATUSBAR_TEXT: COLOR.BRAND.BACKGROUND1,
        },
        [PALETTE_IDS.SKELETON]: {
            SKELETON_COLOR: `${COLOR.BRAND.BACKGROUND2}|${COLOR.BRAND.FOREGROUND3}`,
        },
        [PALETTE_IDS.CHANGED_VALUE_INDICATOR]: {
            CVI_INDICATION_ICON_COLOR: COLOR.BRAND.ACCENT2,
        },
        [PALETTE_IDS.PRICE_BOOST_ITEM]: {
            PBI_PREVIOUS_PRICE_INDICATION_ICON_COLOR: COLOR.BRAND.ACCENT2,
        },
        [PALETTE_IDS.INPUT]: {
            INPUT_BG: COLOR.BRAND.FOREGROUND5,
            INPUT_BORDER_COLOR: COLOR.BRAND.FOREGROUND2,
            INPUT_TEXT: COLOR.BRAND.FOREGROUND1,
            INPUT_LABEL_TEXT: {
                fontSize: FONT.SIZE_MEDIUM,
                color: COLOR.BRAND.FOREGROUND2,
            },
            INPUT_DISABLED_TEXT: COLOR.BRAND.BACKGROUND2,
            INPUT_PLACEHOLDER_TEXT: COLOR.BRAND.FOREGROUND3,
            INPUT_ICON_COLOR: COLOR.BRAND.FOREGROUND2,
            INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND.FOREGROUND2,
            INPUT_DISABLED_BORDER_COLOR: COLOR.BRAND.FOREGROUND6_1,
            ON_DARK: {
                INPUT_BG: 'transparent',
                INPUT_TEXT: COLOR.BRAND.FOREGROUND4,
                INPUT_BORDER_COLOR: COLOR.BRAND.BACKGROUND2,
                INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND.BACKGROUND2,
                INPUT_ICON_COLOR: COLOR.BRAND.BACKGROUND2,
                INPUT_FOCUSED_ICON_COLOR: COLOR.BRAND.FOREGROUND5,
                INPUT_PLACEHOLDER_TEXT: COLOR.BRAND.BACKGROUND2,
                INPUT_LABEL_TEXT: {
                    fontSize: FONT.SIZE_MEDIUM,
                    color: COLOR.BRAND.FOREGROUND4,
                },
            },
        },
        [PALETTE_IDS.STAKE_INPUT]: {
            STAKE_INPUT_BG: COLOR.BRAND.FOREGROUND5,
            STAKE_INPUT_TEXT: COLOR.BRAND.FOREGROUND1,
            STAKE_INPUT_DISABLED_TEXT: COLOR.BRAND.BACKGROUND2,
            STAKE_INPUT_BORDER_COLOR: COLOR.BRAND.FOREGROUND2,
            STAKE_INPUT_FOCUSED_BORDER_COLOR: COLOR.BRAND.FOREGROUND1,
            STAKE_INPUT_DISABLED_BG: COLOR.BRAND.FOREGROUND5,
            STAKE_INPUT_DISABLED_BORDER_COLOR: COLOR.BRAND.BACKGROUND2,
        },
        [PALETTE_IDS.RANGE_INPUT]: {
            RANGE_TRACK_COLOR: COLOR.BRAND.FOREGROUND3,
            RANGE_THUMB_COLOR: COLOR.BRAND.FOREGROUND1,
            RANGE_THUMB_DISABLED_COLOR: COLOR.BRAND.FOREGROUND3,
        },
        [PALETTE_IDS.INFO_NOTIFICATION]: {
            INFO_BG: COLOR.BRAND.BACKGROUND2,
            INFO_BORDER: {
                BORDER_COLOR: COLOR.BRAND.FOREGROUND3,
            },
        },
        [PALETTE_IDS.EMPTY_CONTENT_MESSAGE]: {
            EMPTY_CONTENT_MESSAGE_TEXT: COLOR.BRAND.FOREGROUND2,
        },
        [PALETTE_IDS.SWITCH]: {
            SWITCH_CONTAINER_BG: COLOR.RESERVED.TRANSPARENT,
            SWITCH_CONTAINER_BORDER_COLOR: COLOR.BRAND.FOREGROUND1,
            SWITCH_BUTTON_BG: COLOR.BRAND.FOREGROUND1,
            SWITCH_ICON_COLOR: COLOR.BRAND.BACKGROUND1,
            SWITCH_LABEL_TEXT: COLOR.BRAND.FOREGROUND1,
            SWITCH_DISABLED_CONTAINER_BG: COLOR.RESERVED.TRANSPARENT,
            SWITCH_DISABLED_CONTAINER_BORDER_COLOR: COLOR.BRAND.FOREGROUND3,
            SWITCH_DISABLED_BUTTON_BG: COLOR.BRAND.FOREGROUND3,
            SWITCH_DISABLED_LABEL_TEXT: COLOR.BRAND.FOREGROUND3,
            ON_DARK: {
                SWITCH_CONTAINER_BORDER_COLOR: COLOR.BRAND.FOREGROUND6,
                SWITCH_BUTTON_BG: COLOR.BRAND.BACKGROUND1,
                SWITCH_ICON_COLOR: COLOR.BRAND.FOREGROUND1,
                SWITCH_LABEL_TEXT: COLOR.BRAND.BACKGROUND3,
                SWITCH_DISABLED_CONTAINER_BORDER_COLOR: COLOR.BRAND.FOREGROUND3,
                SWITCH_DISABLED_BUTTON_BG: COLOR.BRAND.FOREGROUND3,
                SWITCH_DISABLED_LABEL_TEXT: COLOR.BRAND.FOREGROUND3,
            },
        },
        [PALETTE_IDS.PROMOTIONS]: {
            BANNER_CONTAINER_BG: 'transparent',
        },
    };

    const tokens = merge(
        {
            custom,
            palette,
        },
        getBaseComponentsConfig(),
        getAccountConfigs(),
        getMenuListsConfigs(),
        getBetHistoryConfig(),
        getBettingCenterConfig(),
        getBonusesConfig(),
        getCardsConfig(),
        getDialogConfig(),
        getEventListConfig(),
        getMarketsConfig(),
        getMatchAccaConfig(),
        getPageHeadersConfigs(),
        getRacingConfig(),
        getStaticPagesConfig(),
        getVirtualsConfig(),
        getSportsContentConfigs()
    );

    return tokens;
}
